import { useMemo } from 'react';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Service, { SERVICES_BUSINESS_AREAS } from 'model/Service';
import { ItemDisplayConfig } from 'components/UI/V3/ItemSelectionDrawer/ItemSelectionDrawer';

/**
 * Display configurations for ItemSelectionDrawer in the Membership context.
 * Unlike the Discounts version, getPrice for services shows the actual price
 * (e.g. "$25" or "$10-$50") instead of "Fixed Price" / "Variable Price".
 * getNumericPrice returns the lowest numeric price for use as the discount
 * field's maxAmount.
 */
export const useMembershipItemDisplayConfigs = () => {
    const serviceDisplay = useMemo<ItemDisplayConfig<Service>>(
        () => ({
            getTitle: (s: Service) => s.name,
            getSubtitle: () => '',
            getPrice: (s: Service): string => {
                if (s.businessArea?.id === SERVICES_BUSINESS_AREAS.TRANSPORTATION) {
                    const fp = s.pricing.fixedPrice?.[0];
                    const pickup = fp?.transportationPrice?.pickUpPrice || 0;
                    const dropoff = fp?.transportationPrice?.dropOffPrice || 0;
                    return pickup === dropoff ? `$${pickup}` : `$${pickup} / $${dropoff}`;
                }

                let first: { price: number } | undefined;
                let last: { price: number } | undefined;

                if (s.pricing.fixedPrice?.length) {
                    first = s.pricing.fixedPrice[0];
                    last = s.pricing.fixedPrice[s.pricing.fixedPrice.length - 1];
                } else if (s.pricing.variablePrice?.prices?.length) {
                    first = s.pricing.variablePrice.prices[0];
                    last = s.pricing.variablePrice.prices[s.pricing.variablePrice.prices.length - 1];
                }

                if (!first) return '$0';
                return first.price === last?.price ? `$${first.price}` : `$${first.price}-$${last?.price}`;
            },
            getNumericPrice: (s: Service): number => {
                if (s.businessArea?.id === SERVICES_BUSINESS_AREAS.TRANSPORTATION) {
                    const fp = s.pricing.fixedPrice?.[0];
                    return fp?.transportationPrice?.pickUpPrice || 0;
                }
                if (s.pricing.fixedPrice?.length) return s.pricing.fixedPrice[0].price;
                if (s.pricing.variablePrice?.prices?.length) return s.pricing.variablePrice.prices[0].price;
                return 0;
            }
        }),
        []
    );

    const addonDisplay = useMemo<ItemDisplayConfig<AddOnService>>(
        () => ({
            getTitle: (a: AddOnService) => a.name,
            getSubtitle: () => 'Add-on Service',
            getPrice: (a: AddOnService) => `$${Number(a.price).toFixed(2)}`,
            getNumericPrice: (a: AddOnService) => Number(a.price)
        }),
        []
    );

    const productDisplay = useMemo<ItemDisplayConfig<Product>>(
        () => ({
            getTitle: (p: Product) => {
                const name = p.businessProduct?.name || p.name;
                return name.length > 30 ? name.substring(0, 30) + '...' : name;
            },
            getSubtitle: (p: Product) => {
                const desc = p.businessProduct?.description;
                if (!desc) return 'Product';
                return desc.length > 50 ? desc.substring(0, 50) + '...' : desc;
            },
            getPrice: (p: Product) =>
                p.businessProduct?.pricing?.price
                    ? `$${Number(p.businessProduct.pricing.price).toFixed(2)}`
                    : 'Price not set',
            getNumericPrice: (p: Product) => Number(p.businessProduct?.pricing?.price || 0),
            getTitleFull: (p: Product) => p.businessProduct?.name || p.name,
            getSubtitleFull: (p: Product) => p.businessProduct?.description || 'Product'
        }),
        []
    );

    return { serviceDisplay, addonDisplay, productDisplay };
};
